import { useEffect, useState } from 'react';
import { Instagram, Calendar, ExternalLink, Award, Star, Trophy, PartyPopper, Facebook } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function EventCard({ event, idx, lang }) {
  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  const link = event.permalink;

  const CardWrapper = ({ children }) => link ? (
    <a href={link} target="_blank" rel="noopener noreferrer"
      className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-amber-300 overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2 block cursor-pointer"
      data-testid={`event-card-${idx}`}>
      {children}
    </a>
  ) : (
    <div className="group bg-white rounded-2xl border-2 border-gray-100 overflow-hidden" data-testid={`event-card-${idx}`}>
      {children}
    </div>
  );

  return (
    <CardWrapper>
      {event.image_url && (
        <div className="relative h-64 overflow-hidden">
          <img
            src={event.image_url}
            alt={event.title || event.caption?.substring(0, 60) || 'Event'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          {event.source === 'instagram' && (
            <div className="absolute top-3 right-3 bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
              <Instagram className="w-3 h-3" /> Instagram
            </div>
          )}
          {(event.timestamp || event.created_at) && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1 text-white/90 text-xs bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
              <Calendar className="w-3 h-3" />
              {formatDate(event.timestamp || event.created_at)}
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        {event.title && (
          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2" style={{ fontFamily: 'Space Grotesk' }}>
            {event.title}
          </h3>
        )}
        {event.caption && (
          <p className="text-gray-600 text-sm line-clamp-4 leading-relaxed mb-4">{event.caption}</p>
        )}
        {link && (
          <span className="inline-flex items-center gap-1.5 text-[#0066b2] text-sm font-medium">
            <ExternalLink className="w-3.5 h-3.5" />
            {lang === 'en' ? 'View on Instagram' : 'Guarda su Instagram'}
          </span>
        )}
      </div>
    </CardWrapper>
  );
}

export default function Events() {
  const [data, setData] = useState({ eventi: [], riconoscimenti: [] });
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/events`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{lang === 'en' ? 'Events & Awards' : 'Eventi e Riconoscimenti'} | Officina Martorana Bosch Car Service</title>
        <meta name="description" content={lang === 'en'
          ? 'Discover events, awards and milestones achieved by Officina Martorana Bosch Car Service.'
          : 'Scopri gli eventi, premi e traguardi raggiunti da Officina Martorana Bosch Car Service – parte del network Bosch Car Service.'} />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 via-white to-blue-50" data-testid="events-hero">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Trophy className="w-4 h-4" />
            {lang === 'en' ? 'Our Story of Excellence' : 'La Nostra Storia di Eccellenza'}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }} data-testid="events-title">
            {lang === 'en' ? 'Events & Awards' : 'Eventi e Riconoscimenti'}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {lang === 'en'
              ? 'A collection of our events, certifications and awards that tell the story of growth and innovation of our workshop.'
              : 'Raccolta dei nostri eventi, certificazioni e riconoscimenti che raccontano la storia di crescita e di innovazione della nostra officina.'}
          </p>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* EVENTI Section */}
          <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="eventi-section">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-[#0066b2]/10 flex items-center justify-center">
                  <PartyPopper className="w-6 h-6 text-[#0066b2]" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                    {lang === 'en' ? 'Events' : 'Eventi'}
                  </h2>
                  <p className="text-gray-500 text-sm">{lang === 'en' ? 'Our latest events and activities' : 'I nostri ultimi eventi e attività'}</p>
                </div>
              </div>

              {data.eventi.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl" data-testid="eventi-empty">
                  <PartyPopper className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{lang === 'en' ? 'Events coming soon...' : 'Eventi in arrivo...'}</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {data.eventi.map((event, idx) => (
                    <EventCard key={event.id} event={event} idx={idx} lang={lang} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Divider */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="border-t border-gray-200" />
          </div>

          {/* RICONOSCIMENTI Section */}
          <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="riconoscimenti-section">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                    {lang === 'en' ? 'Awards' : 'Riconoscimenti'}
                  </h2>
                  <p className="text-gray-500 text-sm">{lang === 'en' ? 'Certifications, awards and milestones' : 'Certificazioni, premi e traguardi'}</p>
                </div>
              </div>

              {data.riconoscimenti.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl" data-testid="riconoscimenti-empty">
                  <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{lang === 'en' ? 'Awards coming soon...' : 'Riconoscimenti in arrivo...'}</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {data.riconoscimenti.map((event, idx) => (
                    <EventCard key={event.id} event={event} idx={`r-${idx}`} lang={lang} />
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Follow CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#833AB4]/5 via-[#E4405F]/5 to-[#FCAF45]/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'en' ? 'Follow Us on Social Media' : 'Seguici sui Social'}
          </h2>
          <p className="text-gray-600 mb-8">
            {lang === 'en'
              ? 'Stay updated on our latest events, awards and daily life at the workshop.'
              : 'Resta aggiornato sui nostri ultimi eventi, riconoscimenti e la vita quotidiana in officina.'}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="https://www.instagram.com/officinamartoranacarservice" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] text-white px-8 py-3 rounded-xl hover:opacity-90 transition-opacity font-medium shadow-lg"
              data-testid="events-follow-instagram">
              <Instagram className="w-5 h-5" />
              Instagram
            </a>
            <a href="https://www.facebook.com/profile.php?id=100063601aborede" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1877F2] text-white px-8 py-3 rounded-xl hover:bg-[#166FE5] transition-colors font-medium shadow-lg"
              data-testid="events-follow-facebook">
              <Facebook className="w-5 h-5" />
              Facebook
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
